import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/destinations", label: "Destinations" },
  { to: "/travel-resources", label: "Travel Resources" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const transparent = pathname === "/" && !scrolled;

  return (
    <header
      data-testid="site-navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FDFBF7]/85 backdrop-blur-md border-b border-stone-200"
          : transparent
          ? "bg-transparent"
          : "bg-[#FDFBF7]/85 backdrop-blur-md border-b border-stone-200"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-[72px]">
          <Link
            to="/"
            data-testid="nav-brand"
            aria-label="Globezing — Home"
            className="inline-flex items-center gap-3 group"
          >
            <img
              src="/globezing-logo-256.png"
              srcSet="/globezing-logo-256.png 1x, /globezing-logo-512.png 2x"
              alt=""
              width="56"
              height="56"
              className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full object-contain transition-all ${
                transparent ? "ring-1 ring-white/40" : ""
              }`}
            />
            <span
              className={`font-serif text-[24px] sm:text-[26px] tracking-tight leading-none transition-colors ${
                transparent ? "text-white" : "text-stone-900"
              }`}
            >
              Globezing<span className="text-[#9c4122]">.</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-[0.18em] uppercase font-medium link-underline transition-colors ${
                    transparent
                      ? "text-white/90 hover:text-white"
                      : isActive
                      ? "text-[#9c4122]"
                      : "text-stone-700 hover:text-stone-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/#newsletter"
              data-testid="nav-subscribe-button"
              className={`text-[13px] tracking-[0.18em] uppercase font-semibold px-5 py-2.5 border transition-all ${
                transparent
                  ? "border-white/80 text-white hover:bg-white hover:text-stone-900"
                  : "border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white"
              }`}
            >
              Subscribe
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className={`md:hidden p-2 ${transparent ? "text-white" : "text-stone-900"}`}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-[#FDFBF7] border-t border-stone-200">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-[0.18em] uppercase font-medium ${
                    isActive ? "text-[#9c4122]" : "text-stone-800"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/#newsletter"
              data-testid="nav-mobile-subscribe"
              className="text-sm tracking-[0.18em] uppercase font-semibold px-5 py-3 border border-stone-900 text-stone-900 text-center"
            >
              Subscribe
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
